import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, UrlSegment } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { RoleEnum } from 'app/modules/shared/enums/roleEnum';
import { User } from 'app/core/user/user.types';
import { AuthUtils } from '../auth.utils';
import { PathEnum } from 'app/modules/shared/enums/pathEnum';
import { of, switchMap } from 'rxjs';
import { UserService } from 'app/core/user/user.service';
import { SubscriptionStatusEnum } from 'app/modules/shared/enums/subscriptionStatusEnum';
import { PartnerService } from 'app/modules/partner/services/partner.service';
import { Subscription } from 'app/modules/shared/models/subscription';
import { UserUtil } from 'app/modules/shared/utils/user.util';
import { SubscriptionService } from 'app/modules/partner/services/subscription.service';

/**
 * Auth Check all these points:
 * 1. is user authenticated
 * 2. is user registration is complete
 * 3. is user role able him to get the page
 * 4. is user subscription status able him to get the page
 * @param route
 * @param state 
 * @returns 
 */
export const AuthGuard: CanActivateFn | CanActivateChildFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) =>
{
    const router = inject(Router);
    const authService = inject(AuthService);
    const partnerService = inject(PartnerService);
    const subscriptionService = inject(SubscriptionService);
    const userService = inject(UserService);

    return authService.check().pipe(
        switchMap((authenticated: boolean) => {
            // 1. Check if the user is authenticated
            if (!authenticated) {

                // Clear the user session
                authService.signOut();

                // Redirect to the sign-in page with a redirectUrl param
                const urlTree = router.parseUrl(PathEnum.AUTH_SIGN_IN + `?redirectURL=${buildUrlPath(state)}`);
                return of(urlTree);
            } else {
                // 2. Check if the user registration is complete
                const partnerObs = partnerService.partner$;
                return partnerObs.pipe(
                    switchMap(partner => {
                        if (!partner) {
                            console.error("Erreur d'initialisation de l'application");
                            return of(router.createUrlTree([PathEnum.ERROR, PathEnum.ERROR_500]));
                        }

                        // A partner without a current subscription (not in normal flows: signup creates the Pro
                        // trial) chooses a plan first, on the plan choice page (stepper-sign); the wizard and the
                        // app come next. Read on the partner: without a subscription the app initializer loads
                        // none, and check 4 below would wait for it forever.
                        if (!partner.subscriptionId) {
                            return of(router.createUrlTree([PathEnum.AUTH_STEPPER_SIGN]));
                        }

                        // Check if we are already on stepper or wizard pages
                        const isWizardUrl = router.url === PathEnum.BASE_URL + PathEnum.AUTH_WIZARD_SIGN;

                        // Redirect to wizard if first steps not completed
                        if (!partner.firstStepsCompleted && !isWizardUrl) {
                            return of(router.createUrlTree([PathEnum.AUTH_WIZARD_SIGN]));
                        }

                        // 3. Check if the user has the mandatory role
                        const userObs = userService.user$;
                        return userObs.pipe(
                            switchMap(user => {
                                if (!user) {
                                    console.error("Erreur d'initialisation de l'application");
                                    return of(router.createUrlTree([PathEnum.ERROR, PathEnum.ERROR_500]));
                                }
                                const allowedRoles = route.data['allowedRoles'] as RoleEnum[];
                                const res = checkUserRoles(allowedRoles);
                                if(!res){
                                    console.error("User don't have the roles for this page");
                                    return of(router.createUrlTree([PathEnum.ERROR, PathEnum.ERROR_403]));
                                }
                                // 4. Check if the user has the mandatory partnerSubscription status
                                const partnerSubscriptionObs = subscriptionService.partnerSubscription$;
                                return partnerSubscriptionObs.pipe(
                                    switchMap(partnerSubscription => {
                                        if (!partnerSubscription) {
                                            // No current subscription after all: the plan choice page, as above
                                            return of(router.createUrlTree([PathEnum.AUTH_STEPPER_SIGN]));
                                        }
                                        const allowedSubscriptionStatus = route.data['allowedSubscriptionStatus'] as SubscriptionStatusEnum[];
                                        const res = checkUserSubscription(partnerSubscription, allowedSubscriptionStatus);
                                        if(!res){
                                            console.error("User don't have the subscription status for this page");
                                            return of(router.createUrlTree([PathEnum.ERROR, PathEnum.ERROR_403]));
                                        }
                                        // Continuer avec d'autres vérifications si nécessaire
                                        return of(res);
                                    })
                                );
                            })
                        );
                    })
                );
            }
        })
    );
};

function buildUrlPath(state: UrlSegment[] | RouterStateSnapshot) {
    let path = '';
    if(Array.isArray(state)){
        let i: number = 0;
        while (i < state.length) {
            path += state[i].path + '/';
            i++;
        }
        path = path.substring(0, path.length - 1);
    } else {
        path = state.url;
    }

    return path;
}

function checkUserRoles(allowedRoles: string[]): boolean {
    const user: User = AuthUtils.getUserToken(); // Assuming this method synchronously returns the user token

    // Check if all users can access
    if (!allowedRoles || allowedRoles.length === 0 || allowedRoles.includes(RoleEnum.ALL)) {
        return true;
    }

    // Check if all partner users can access
    if (!allowedRoles || allowedRoles.length === 0 || (UserUtil.isPartnerUser(user) && allowedRoles.includes(RoleEnum.APR))) {
        return true;
    }

    const userHasRequiredRole = user.roles.some(role => allowedRoles.includes(role as RoleEnum));

    if (userHasRequiredRole) {
        return true;
    } else {
        return false;
    }
}


function checkUserSubscription(partnerSubscription: Subscription, allowedSubscriptionStatus: SubscriptionStatusEnum[]): boolean{
    let isCheck : boolean = true; // Default true for none protected pages

    if (allowedSubscriptionStatus) {
        if (allowedSubscriptionStatus.includes(SubscriptionStatusEnum.ALL)) {
            isCheck = true;
        }
        const isAuthorizedStatus = hasRequiredSubscriptionStatus(partnerSubscription, allowedSubscriptionStatus);
        if (!isAuthorizedStatus) {
            isCheck = false;
        } else {
            isCheck = true;
        }
    }

    return isCheck;
}

/**
 * Check if the user has the required subscription status
 * @param status 
 * @returns 
 */
function hasRequiredSubscriptionStatus(partnerSubscription: Subscription, status: string | string[]) {
    const currentSubscriptionStatus = partnerSubscription.subscriptionStatusRefCode;
    if (typeof status === 'string') {
        status = [status];
    }
    for (const roleSlug of status) {
        if (roleSlug === SubscriptionStatusEnum.ALL) {
            return true;
        }
        if (currentSubscriptionStatus === roleSlug) {
            return true;
        }
    }
    return false;
}
